import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  VStack,
  Text,
  InputGroup,
  InputLeftElement,
  Input,
  LinkBox,
  LinkOverlay,
} from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useDebounce } from 'usehooks-ts';

import PlusIcon from '../../../assets/icons/plus.svg';
import { useGetAllTeamMembersQuery } from '../../../services';
import { CenteredSpinner, RoundedBadge } from '../components';
import { ReactComponent as LeftArrowIcon } from '../../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrowIcon } from '../../../assets/icons/right-arrow.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';

export const Team: FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 300);

  const params = useParams();
  const { companyId } = params;
  const status = params.status || 'all';

  const navigate = useNavigate();

  const { data, isLoading, refetch } = useGetAllTeamMembersQuery({
    companyId: Number(companyId),
    page,
    name: debouncedSearchTerm,
  });

  useEffect(() => {
    void refetch();
  }, [debouncedSearchTerm, refetch]);

  return (
    <Box display="flex" flexDirection="column" flex="1">
      <HStack justify="space-between" mt="4" align="start">
        <HStack align="baseline">
          <Heading color="custom_blue.1" fontSize="2xl">
            Team
          </Heading>
        </HStack>

        <Button
          onClick={() =>
            navigate(`/companies/${status}/${companyId}/team/new-member`)
          }
          bg="custom_orange.2"
          _hover={{ bg: 'custom_orange.2' }}
          color="white"
          fontWeight="semibold"
          fontSize="sm"
        >
          <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
          New Team Member
        </Button>
      </HStack>

      <HStack pt="4" pb="2">
        <InputGroup flexBasis="45%">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.300" />
          </InputLeftElement>
          <Input
            placeholder="Search"
            onChange={e => setSearchTerm(e.target.value)}
          />
        </InputGroup>

        <Button
          leftIcon={<FiltersIcon />}
          color="custom_blue.1"
          fontWeight="semibold"
          fontSize="sm"
          boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
          border="1px"
          borderColor="custom_blue.3"
          bg="none"
          _hover={{ bg: 'none' }}
        >
          Filters
        </Button>
      </HStack>

      {isLoading ? (
        <CenteredSpinner />
      ) : (
        <Box>
          <TableContainer
            my="4"
            boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
            borderRadius="md"
            border="1px"
            borderColor="custom_gray.3"
            minH="65vh"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th
                    border="none"
                    color="custom_blue.1"
                    fontWeight="medium"
                    fontSize="xs"
                    letterSpacing="normal"
                    textTransform="none"
                  >
                    Member
                  </Th>
                  <Th
                    border="none"
                    color="custom_blue.1"
                    fontWeight="medium"
                    fontSize="xs"
                    letterSpacing="normal"
                    textTransform="none"
                  >
                    E-mail Address
                  </Th>
                  <Th
                    border="none"
                    color="custom_blue.1"
                    fontWeight="medium"
                    fontSize="xs"
                    letterSpacing="normal"
                    textTransform="none"
                  >
                    Status
                  </Th>
                  <Th
                    border="none"
                    color="custom_blue.1"
                    fontWeight="medium"
                    fontSize="xs"
                    letterSpacing="normal"
                    textTransform="none"
                  >
                    Phone Number
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {data?.data.content.map(
                  ({
                    id,
                    firstName,
                    lastName,
                    email,
                    countryCode,
                    phoneNumber,
                    status,
                  }) => (
                    <LinkBox key={id} as={Tr}>
                      <Td border="none">
                        <HStack>
                          <Box
                            w="10"
                            h="10"
                            bg="custom_blue.1"
                            borderRadius="full"
                          >
                            {/* <Image src={companyAvatar} borderRadius="full" /> */}
                          </Box>
                          <VStack align="flex-start">
                            <Text
                              color="custom_blue.1"
                              fontWeight="semibold"
                              fontSize="sm"
                            >
                              <LinkOverlay as={Link} to={String(id)}>
                                {firstName} {lastName}
                              </LinkOverlay>
                            </Text>
                            <Text color="custom_blue.1" fontSize="xs">
                              Product Designer
                            </Text>
                          </VStack>
                        </HStack>
                      </Td>
                      <Td border="none">
                        <RoundedBadge colorSchema={status}>
                          {status}
                        </RoundedBadge>
                      </Td>
                      <Td border="none">{email}</Td>

                      <Td
                        color="custom_blue.1"
                        fontWeight="medium"
                        fontSize="xs"
                        border="none"
                      >
                        {countryCode} {phoneNumber}
                      </Td>
                    </LinkBox>
                  )
                )}
              </Tbody>
            </Table>

            <Box px="6" pb="3">
              <ReactPaginate
                breakLabel="..."
                pageRangeDisplayed={2}
                // initialPage={0} // TODO: add dynamically
                marginPagesDisplayed={3}
                containerClassName="paginator"
                pageClassName="paginator-item"
                pageCount={data?.data.totalPages}
                previousLabel={
                  <Button
                    leftIcon={<LeftArrowIcon />}
                    color="custom_blue.1"
                    fontWeight="semibold"
                    fontSize="sm"
                    boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                    border="1px"
                    borderColor="custom_blue.3"
                    bg="none"
                    _hover={{ bg: 'none' }}
                  >
                    previous
                  </Button>
                }
                nextLabel={
                  <Button
                    rightIcon={<RightArrowIcon />}
                    color="custom_blue.1"
                    fontWeight="semibold"
                    fontSize="sm"
                    boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
                    border="1px"
                    borderColor="custom_blue.3"
                    bg="none"
                    _hover={{ bg: 'none' }}
                  >
                    Next
                  </Button>
                }
                renderOnZeroPageCount={null}
                onPageChange={e => setPage(e.selected)}
              />
            </Box>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};
